import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DoCheck,
  effect,
  forwardRef,
  input,
  model,
  signal,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { HxBaseControl } from '../../base';
import { createValidationState, ValidationState } from '../../base/validation-state';
import { HxError } from '../form-group';
import { HxFormGroup } from '../form-group/form-group';
import { HxInput } from '../input';
import { HxLabel } from '../label/label';
import { HxRadio } from '../radio/radio';
import { HxRadioGroup } from '../radio/radio-group';
/**
 * Radio group component with an additional "Other" input field. Include 'Other' in the options array to enable the
 * input field functionality. When the "Other" option is selected, an input field appears below the radio buttons
 * allowing users to specify a custom value. Matches the checkbox-with-label pattern for "Other" functionality.
 *
 * @example
 *   ```html
 *   <!-- Basic usage with "Other" option -->
 *   <hx-radio-with-other
 *     [(value)]="selectedOption"
 *     [(otherValue)]="otherText"
 *     [options]="['Option A', 'Option B', 'Other']">
 *   </hx-radio-with-other>
 *
 *   <!-- With custom labels and properties -->
 *   <hx-radio-with-other
 *     [(value)]="selectedOption"
 *     [(otherValue)]="otherText"
 *     [options]="['Yes', 'No', 'Other']"
 *     [otherLabel]="'Custom Option'"
 *     [reasonLabel]="'Please explain'"
 *     [reasonPlaceholder]="'Enter details...'"
 *     [reasonMaxLength]="50"
 *     [showInputOnOther]="true">
 *   </hx-radio-with-other>
 *
 *   <!-- Without input field -->
 *   <hx-radio-with-other
 *     [(value)]="selectedOption"
 *     [options]="['Email', 'Phone']"
 *     [showInputOnOther]="false">
 *   </hx-radio-with-other>
 *   ```;
 *
 * @feature 008-radio
 */
@Component({
  selector: 'hx-radio-with-other',
  templateUrl: './radio-with-other.html',
  styleUrls: ['./radio-with-other.css'],
  standalone: true,
  imports: [
    HxRadioGroup,
    HxRadio,
    ReactiveFormsModule,
    HxFormGroup,
    HxInput,
    HxError,
    HxLabel,
    NgClass,
    NgTemplateOutlet,
  ],
  host: {
    class: 'hx-radio-with-other',
    '[class.hx-radio-with-other--label-horizontal]': 'labelHorizontally() && orientation() === "horizontal"',
    '[class.hx-radio-with-other--readonly]': 'readonly()',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HxRadioWithOther),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => HxRadioWithOther),
      multi: true,
    },
  ],
})
export class HxRadioWithOther<T extends string = string>
  extends HxBaseControl
  implements ControlValueAccessor, Validator, DoCheck
{
  /**
   * Question text to display above the radio group.
   *
   * @default ''
   */
  readonly question = input<string>('');

  /**
   * Sublabel text to display below the question.
   *
   * @default ''
   */
  readonly sublabel = input<string>('');

  /**
   * The radio button options to display. Include 'Other' in the array if you want to enable the "Other" input field.
   *
   * @example
   *   ```html
   *   <hx-radio-with-other [options]="['Yes', 'No', 'Other']"></hx-radio-with-other>
   *   ```;
   *
   * @required
   */
  readonly options = input.required<T[]>();

  /**
   * Currently selected radio value. Supports two-way binding with [(value)].
   *
   * @default null
   */
  readonly value = model<T | null>(null);

  /**
   * The text entered in the "Other" input field. Only populated when value is 'Other'. Supports two-way binding with
   * [(otherValue)].
   *
   * @default ''
   */
  readonly otherValue = model<string>('');

  /**
   * Label for "Other" option that triggers reason field.
   *
   * @default 'Other'
   */
  readonly otherLabel = input<string>('Other');

  /**
   * Label for reason text field when Other is selected.
   *
   * @default 'Please specify'
   */
  readonly reasonLabel = input<string>('Please specify');

  /**
   * Whether the "Other" input field should be multi-line.
   *
   * @default false
   */
  readonly otherInputMultiline = input<boolean>(false);
  /**
   * Placeholder text for the reason input field.
   *
   * @default 'Enter your reason...'
   */
  readonly reasonPlaceholder = input<string>('Enter your reason...');

  /**
   * Maximum character length for reason text field.
   *
   * @default 30
   */
  readonly reasonMaxLength = input<number>(30);

  /**
   * Whether to show input field when "Other" option is selected.
   *
   * @default true
   */
  readonly showInputOnOther = input<boolean>(true);

  /**
   * Custom error message for required reason field.
   *
   * @default ''
   */
  readonly reasonRequiredErrorMessage = input<string>('This field is required');

  /**
   * Layout orientation for the radio group.
   *
   * - 'vertical': Stacks radios in a column (default)
   * - 'horizontal': Arranges radios in a row
   *
   * @default 'vertical'
   */
  readonly orientation = input<'vertical' | 'horizontal'>('vertical');

  /**
   * Number of columns for grid layout. When set, radios render in a CSS Grid with the specified column count. When
   * undefined, uses flexbox layout based on orientation.
   *
   * @example
   *   ```html
   *   <hx-radio-with-other
   *     [options]="['Yes', 'No', 'Maybe']"
   *     [columns]="2">
   *   </hx-radio-with-other>
   *   ```;
   *
   * @default undefined (uses flexbox)
   */
  readonly columns = input<number | undefined>(undefined);

  /**
   * Evenly distributes radios horizontally across the row using space-evenly. Works with horizontal orientation (flex
   * layout) or grid layout (centers items in cells).
   *
   * @default false
   */
  readonly justify = input<boolean>(false);

  /**
   * Sort order for radio options. Applies to all options including All/None/Other.
   *
   * - 'asc': Sort alphabetically A→Z
   * - 'desc': Sort alphabetically Z→A
   * - Null/undefined: No sorting (insertion order)
   *
   * @default null
   */
  readonly sortOrder = input<'asc' | 'desc' | null>(null);

  /**
   * Places the question label horizontally to the left of the radio group. Creates a two-column layout: Question Label
   * (1/3 width) and Radios (2/3 width). Only applies when orientation is 'horizontal'.
   *
   * @default false
   */
  readonly labelHorizontally = input<boolean>(false);

  /**
   * Custom error message when required validation fails. Passed through to the underlying radio group.
   *
   * @default ''
   */
  readonly requiredErrorMessage = input<string>('');

  /**
   * FormControl for the "Other" input field. Parent must create and manage this control for validation.
   *
   * @default undefined (no external control)
   */
  otherControl = input<FormControl<string> | null>(null);

  /** Optional template rendered after each radio option. Context provides the option value as `$implicit`. */
  readonly optionTemplate = input<TemplateRef<{ $implicit: T }> | null>(null);

  /**
   * Position of sub-controls under horizontal layout. 'start' = Rendered below all options (order 2, full width)
   * 'beneath' = Rendered directly beneath the parent option (inline)
   */
  readonly subControlPosition = input<'start' | 'beneath'>('start');

  /**
   * Ordered options with fixed rule: None, All, sorted regular options, Other. All/None/Other are never affected by
   * sorting.
   */
  protected readonly _orderedOptions = computed(() => {
    const opts = this.options();
    const order = this.sortOrder();

    const normalize = (value: T): string => String(value).trim().toLowerCase();
    const isAll = (value: T): boolean => normalize(value) === 'all';
    const isNone = (value: T): boolean => normalize(value) === 'none';
    const isOther = (value: T): boolean => normalize(value) === 'other';

    const all = opts.find(isAll) ?? null;
    const none = opts.find(isNone) ?? null;
    const other = opts.find(isOther) ?? null;

    const regular = opts.filter((opt) => !isAll(opt) && !isNone(opt) && !isOther(opt));
    const sortedRegular = !order
      ? regular
      : [...regular].sort((a, b) => {
          const labelA = String(a);
          const labelB = String(b);
          const cmp = labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
          return order === 'asc' ? cmp : -cmp;
        });

    const ordered: T[] = [];
    if (none) ordered.push(none);
    if (all) ordered.push(all);
    ordered.push(...sortedRegular);
    if (other) ordered.push(other);

    return ordered;
  });

  /** Computed property to check if the selected value is 'Other'. Used for displaying the input field. */
  protected readonly _isOtherSelected = computed(() => {
    if (!this.showInputOnOther()) {
      return false;
    }

    const currentValue = this.value();
    if (!currentValue) {
      return false;
    }

    // Check if the selected value is "other" (case-insensitive)
    // This works for both standalone usage and when used with formControl
    const valueStr = String(currentValue).toLowerCase();
    return valueStr === 'other' || valueStr.includes('other');
  });

  /** Internal FormControl for managing validation state */
  protected _internalControl = new FormControl<T | null>(null);

  /**
   * Unified validation state - SINGLE SOURCE OF TRUTH Replaces the previous scattered signals (_invalid, _touched,
   * etc.)
   */
  protected _validationState!: ValidationState;

  /** Local signal that mirrors showErrors() for reactive host class binding. */
  protected readonly _showErrors = signal<boolean>(false);

  /** Tracks the previous touched state of external control. Used in DoCheck to detect programmatic touch changes. */
  private _previousExternalTouched = false;

  /** Computed signal for error state */
  protected _hasError = computed(() => this._showErrors());

  /** Private callbacks for ControlValueAccessor */
  private _onChangeCallback = (value: T | null) => {};
  private _onTouchedCallback = () => {};

  // ===== CONSTRUCTOR & LIFECYCLE =====

  constructor() {
    super();

    // Initialize unified validation state - SINGLE SOURCE OF TRUTH
    this._validationState = createValidationState({
      internalControl: this._internalControl,
      getNgControl: () => this.ngControl,
    });

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    // Sync FormControl validators when inputs change
    effect(() => {
      const control = this._internalControl;
      const validators = this._buildValidators();
      control.setValidators(validators);
      control.updateValueAndValidity({ emitEvent: false });

      // Update validation state after validator changes
      this._validationState.updateAfterValueChange();

      this._validatorChangeFn();
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // Sync initial state from external control if available
    this._validationState.syncFromExternalControl();
    this._syncShowErrors();

    // Subscribe to external control changes to sync _showErrors signal
    // This handles programmatic changes like form.markAllAsTouched()
    const externalControl = this.ngControl?.control;
    if (externalControl) {
      externalControl.statusChanges?.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
        this._syncShowErrors();
      });
    }
  }

  /**
   * DoCheck lifecycle hook to detect programmatic touch state changes. This is critical for handling
   * form.markAllAsTouched() which doesn't trigger statusChanges.
   */
  ngDoCheck(): void {
    const externalControl = this.ngControl?.control;
    if (externalControl) {
      const currentTouched = externalControl.touched;
      // If touched state changed externally (e.g., form.markAllAsTouched())
      if (currentTouched !== this._previousExternalTouched) {
        this._previousExternalTouched = currentTouched;
        this._syncShowErrors();
      }
    }
  }

  /** Syncs the _showErrors signal with validation state */
  private _syncShowErrors(): void {
    this._showErrors.set(this._validationState.showErrors());
  }

  // ===== ControlValueAccessor Implementation =====

  /** ControlValueAccessor: Called when parent form control value changes */
  writeValue(value: T | null): void {
    this.value.set(value);
    this._internalControl.setValue(value, { emitEvent: false });

    // Update validation state
    this._validationState.updateAfterValueChange();
  }

  /** ControlValueAccessor: Register callback for value changes */
  registerOnChange(fn: (value: T | null) => void): void {
    this._onChangeCallback = fn;
  }

  /** ControlValueAccessor: Register callback for blur events */
  registerOnTouched(fn: () => void): void {
    this._onTouchedCallback = fn;
  }

  /** ControlValueAccessor: Set disabled state */
  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this._internalControl.disable() : this._internalControl.enable();
  }

  // ===== Internal Methods =====

  /**
   * Emit the current form value to parent form control.
   *
   * @internal
   */
  protected _emitValue(): void {
    this._onChangeCallback(this.value());
  }

  /**
   * When radio value changes, emit to parent and clear otherValue if needed.
   *
   * @internal
   */
  protected _onRadioChange(newValue: T | null): void {
    this.value.set(newValue);
    const isOtherOption = String(newValue).toLowerCase() === this.otherLabel().toLowerCase();
    if (!isOtherOption) {
      this.otherValue.set('');
      const oc = this.otherControl();
      if (oc) {
        oc.setValue('', { emitEvent: false });
      }
    }

    // Update internal control for validation
    this._internalControl.setValue(newValue, { emitEvent: false });
    this._internalControl.markAsTouched();
    this._internalControl.updateValueAndValidity();

    // Update validation state
    this._validationState.markTouched();
    this._syncShowErrors();

    this._emitValue();
    this._onTouchedCallback();
  }

  /**
   * When other input value changes, emit to parent.
   *
   * @internal
   */
  protected _onOtherValueChange(value: string): void {
    this.otherValue.set(value);
  }

  // ===== VALIDATOR IMPLEMENTATION =====

  validate(control: AbstractControl): ValidationErrors | null {
    return this._internalControl.errors;
  }

  registerOnValidatorChange(fn: () => void): void {
    this._validatorChangeFn = fn;
  }

  // ===== PRIVATE METHODS =====

  /** Builds validator array based on component inputs */
  private _buildValidators(): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (this.required()) {
      validators.push(Validators.required);
    }

    return validators;
  }

  /** Gets error message for display */
  protected _getErrorMessage(errorType: string): string {
    const errors = this._internalControl.errors;
    if (!errors) return '';

    switch (errorType) {
      case 'required':
        return this.requiredErrorMessage() || 'Please select an option';

      default:
        return '';
    }
  }
}
